/**
 * DisclosureSection
 *
 * Lightweight accordion section used throughout the review flow. Provides a
 * toggleable header with optional summary/completion badge and renders
 * children in a smoothly expanding body.
 */
import { ChevronDown } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';

/** Props for `DisclosureSection` (accordion-like block). */
interface DisclosureSectionProps {
  /** Section title displayed in the header. */
  title: string;
  /** Optional one-line summary shown when collapsed. */
  summary?: string;
  /** Whether the content is expanded. */
  isOpen: boolean;
  /** Indicates the section has no remaining required items. */
  isComplete?: boolean;
  /** Toggle open/closed state. */
  onToggle: () => void;
  /** Optional handler for marking section as reviewed. */
  onMarkComplete?: () => void;
  /** Optional custom actions to render in the header (e.g., buttons). */
  headerActions?: React.ReactNode;
  /** Section contents. */
  children: React.ReactNode;
}

export const DisclosureSection: React.FC<DisclosureSectionProps> = ({
  title,
  summary,
  isOpen,
  isComplete,
  onToggle,
  onMarkComplete,
  headerActions,
  children,
}) => {
  return (
    <div className="rounded border bg-white shadow-sm">
      <div
        className={cn(
          'flex items-center justify-between px-3 py-3 bg-white gap-3',
          !isOpen && 'border-b',
        )}
      >
        <button
          type="button"
          className="flex-1 flex items-center gap-2 min-w-0 hover:bg-gray-50 -mx-3 px-3 py-1 rounded"
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform flex-shrink-0',
              isOpen ? 'rotate-180' : '',
            )}
          />
          <div className="min-w-0">
            <div className="text-[17px] font-medium text-zinc-900 truncate">
              {title}
            </div>
            {!isOpen && summary && <div className="hidden">{summary}</div>}
          </div>
        </button>
        {headerActions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {headerActions}
          </div>
        )}
      </div>
      <div
        className={cn(
          'overflow-hidden transition-[max-height,opacity] duration-200',
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="bg-white">{children}</div>
      </div>
    </div>
  );
};
